import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const MarginAnalysisSection = ({ formData, handleInputChange, errors }) => {
  const calculateGrossMargin = () => {
    const revenue = parseFloat(formData?.totalRevenue) || 0;
    const cogs = parseFloat(formData?.costOfGoodsSold) || 0;
    if (revenue === 0) return 0;
    return (((revenue - cogs) / revenue) * 100)?.toFixed(1);
  };

  const calculateOperatingMargin = () => {
    const revenue = parseFloat(formData?.totalRevenue) || 0;
    const operatingExpenses = parseFloat(formData?.totalOperatingExpenses) || 0;
    if (revenue === 0) return 0;
    return (((revenue - operatingExpenses) / revenue) * 100)?.toFixed(1);
  };

  const calculateNetMargin = () => {
    const revenue = parseFloat(formData?.totalRevenue) || 0;
    const totalExpenses = parseFloat(formData?.totalExpenses) || 0;
    if (revenue === 0) return 0;
    return (((revenue - totalExpenses) / revenue) * 100)?.toFixed(1);
  };

  const getMarginStatus = (margin) => {
    if (margin >= 20) return { color: 'success', label: 'Sangat Baik', icon: 'TrendingUp' };
    if (margin >= 10) return { color: 'primary', label: 'Baik', icon: 'ArrowUp' };
    if (margin >= 5) return { color: 'warning', label: 'Cukup', icon: 'Minus' };
    return { color: 'error', label: 'Rendah', icon: 'TrendingDown' };
  };

  const grossMargin = parseFloat(calculateGrossMargin());
  const operatingMargin = parseFloat(calculateOperatingMargin());
  const netMargin = parseFloat(calculateNetMargin());

  const grossStatus = getMarginStatus(grossMargin);
  const operatingStatus = getMarginStatus(operatingMargin);
  const netStatus = getMarginStatus(netMargin);

  return (
    <div className="card">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center flex-shrink-0">
          <Icon name="PieChart" size={24} color="var(--color-success)" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl font-semibold mb-2">Analisis Margin Keuntungan</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Evaluasi profitabilitas dan efisiensi operasional bisnis
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Input
          label="Total Pendapatan"
          type="number"
          placeholder="0"
          description="Pendapatan kotor bulanan"
          value={formData?.totalRevenue || ''}
          onChange={(e) => handleInputChange('totalRevenue', null, e?.target?.value)}
          error={errors?.totalRevenue}
          required
        />

        <Input
          label="Harga Pokok Penjualan (HPP)"
          type="number"
          placeholder="0"
          description="Biaya langsung produksi"
          value={formData?.costOfGoodsSold || ''}
          onChange={(e) => handleInputChange('costOfGoodsSold', null, e?.target?.value)}
          error={errors?.costOfGoodsSold}
          required
        />

        <Input
          label="Total Biaya Operasional"
          type="number"
          placeholder="0"
          description="Semua biaya operasional"
          value={formData?.totalOperatingExpenses || ''}
          onChange={(e) => handleInputChange('totalOperatingExpenses', null, e?.target?.value)}
          error={errors?.totalOperatingExpenses}
          required
        />

        <Input
          label="Total Pengeluaran"
          type="number"
          placeholder="0"
          description="HPP + Operasional + Lainnya"
          value={formData?.totalExpenses || ''}
          onChange={(e) => handleInputChange('totalExpenses', null, e?.target?.value)}
          error={errors?.totalExpenses}
          required
        />
      </div>
      <div className="space-y-4">
        <div className={`p-4 md:p-6 bg-${grossStatus?.color}/10 rounded-lg border-2 border-${grossStatus?.color}/20`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-${grossStatus?.color}/20 flex items-center justify-center`}>
                <Icon name={grossStatus?.icon} size={20} color={`var(--color-${grossStatus?.color})`} />
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold">Margin Kotor (Gross Margin)</h3>
                <p className="text-sm text-muted-foreground">Pendapatan - HPP</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl md:text-3xl font-bold data-text">{grossMargin}%</p>
              <span className={`text-sm font-medium text-${grossStatus?.color}`}>{grossStatus?.label}</span>
            </div>
          </div>
        </div>

        <div className={`p-4 md:p-6 bg-${operatingStatus?.color}/10 rounded-lg border-2 border-${operatingStatus?.color}/20`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-${operatingStatus?.color}/20 flex items-center justify-center`}>
                <Icon name={operatingStatus?.icon} size={20} color={`var(--color-${operatingStatus?.color})`} />
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold">Margin Operasional</h3>
                <p className="text-sm text-muted-foreground">Pendapatan - Biaya Operasional</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl md:text-3xl font-bold data-text">{operatingMargin}%</p>
              <span className={`text-sm font-medium text-${operatingStatus?.color}`}>{operatingStatus?.label}</span>
            </div>
          </div>
        </div>

        <div className={`p-4 md:p-6 bg-${netStatus?.color}/10 rounded-lg border-2 border-${netStatus?.color}/20`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-${netStatus?.color}/20 flex items-center justify-center`}>
                <Icon name={netStatus?.icon} size={20} color={`var(--color-${netStatus?.color})`} />
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold">Margin Bersih (Net Margin)</h3>
                <p className="text-sm text-muted-foreground">Keuntungan setelah semua biaya</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl md:text-3xl font-bold data-text">{netMargin}%</p>
              <span className={`text-sm font-medium text-${netStatus?.color}`}>{netStatus?.label}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Standar Industri Dairy:</p>
            <p>Gross Margin: 30-40% | Operating Margin: 15-25% | Net Margin: 8-15%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarginAnalysisSection;